import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "./styles/globalButtons.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./context/ThemeContext";
import Layout from "./components/Layout";
import { Toaster } from "./components/ui/toaster";

// Eagerly load Home since it's the landing page (LCP-critical)
import Home from "./pages/Home";

// Lazy load all other public pages
const Therapies = lazy(() => import("./pages/Therapies"));
const Trainings = lazy(() => import("./pages/Trainings"));
const TrainingDetail = lazy(() => import("./pages/TrainingDetail"));
const Events = lazy(() => import("./pages/Events"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Team = lazy(() => import("./pages/Team"));
const Vlogs = lazy(() => import("./pages/Vlogs"));
const Contact = lazy(() => import("./pages/Contact"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const HomeOption1 = lazy(() => import("./pages/HomeOption1"));
const HomeOption2 = lazy(() => import("./pages/HomeOption2"));

// Lazy load ALL admin pages (never needed by public visitors)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTherapies = lazy(() => import("./pages/admin/AdminTherapies"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminTrainings = lazy(() => import("./pages/admin/AdminTrainings"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminVlogs = lazy(() => import("./pages/admin/AdminVlogs"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminAbout = lazy(() => import("./pages/admin/AdminAbout"));
const AdminHero = lazy(() => import("./pages/admin/AdminHero"));
const AdminPhilosophy = lazy(() => import("./pages/admin/AdminPhilosophy"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminNavigation = lazy(() => import("./pages/admin/AdminNavigation"));
const AdminTheme = lazy(() => import("./pages/admin/AdminTheme"));
const AdminPages = lazy(() => import("./pages/admin/AdminPages"));
const AdminPageHeroes = lazy(() => import("./pages/admin/AdminPageHeroes"));
const AdminHomeSections = lazy(() => import("./pages/admin/AdminHomeSections"));
const AdminPageSettings = lazy(() => import("./pages/admin/AdminPageSettings"));
const AdminCTASettings = lazy(() => import("./pages/admin/AdminCTASettings"));
const AdminCRM = lazy(() => import("./pages/admin/AdminCRM"));
const TestImageUpload = lazy(() => import("./pages/admin/TestImageUpload"));

// Minimal loading fallback (no spinner to avoid CLS)
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 border-3 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/option1" element={<HomeOption1 />} />
                  <Route path="/option2" element={<HomeOption2 />} />
                  <Route path="/therapies" element={<Therapies />} />
                  <Route path="/trainings" element={<Trainings />} />
                  <Route path="/trainings/:trainingId" element={<TrainingDetail />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  <Route path="/vlogs" element={<Vlogs />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  {/* Payment Routes */}
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  
                  {/* Legal Pages */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/therapies" element={<AdminTherapies />} />
                  <Route path="/admin/events" element={<AdminEvents />} />
                  <Route path="/admin/trainings" element={<AdminTrainings />} />
                  <Route path="/admin/team" element={<AdminTeam />} />
                  <Route path="/admin/testimonials" element={<AdminTestimonials />} />
                  <Route path="/admin/vlogs" element={<AdminVlogs />} />
                  <Route path="/admin/reviews" element={<AdminReviews />} />
                  <Route path="/admin/hero" element={<AdminHero />} />
                  <Route path="/admin/philosophy" element={<AdminPhilosophy />} />
                  <Route path="/admin/about" element={<AdminAbout />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/page-heroes" element={<AdminPageHeroes />} />
                  <Route path="/admin/home-sections" element={<AdminHomeSections />} />
                  <Route path="/admin/page-settings" element={<AdminPageSettings />} />
                  <Route path="/admin/cta-settings" element={<AdminCTASettings />} />
                  <Route path="/admin/crm" element={<AdminCRM />} />
                  <Route path="/admin/navigation" element={<AdminNavigation />} />
                  <Route path="/admin/theme" element={<AdminTheme />} />
                  <Route path="/admin/pages" element={<AdminPages />} />
                  <Route path="/admin/test-upload" element={<TestImageUpload />} />
                </Routes>
              </Suspense>
            </Layout>
          </BrowserRouter>
          <Toaster />
        </div>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
